
import {bulkUpdate} from 'utils/bulkUpdate.js';
import Tone from 'tone';
import Effects from 'effects/Effects.js';
import {AudioParamValueParameter} from 'parameters/AbstractAudioParamParameter.js';
import {AbstractParameter} from 'parameters/AbstractParameter.js';
import Diode from './Diode.js';

class RingModulator extends Effects.Effect {
    constructor(sequencerEngine) {
        super("Ring Modulator", () => new Tone.Gain(0.5), sequencerEngine);

        let context = Tone.Master.context;

        this.dryIn = this.effectNode;
        this.wetIn = new Tone.Gain(0.5);
        this.dryIn.connect(this.wetIn);
        this.cross = new Tone.CrossFade(0.5);
        this.dryIn.connect(this.cross, 0, 0);
        this.outGain = new Tone.Gain(1);
        this.outGain.connect(this.cross, 0, 1);

        this.wetParameter = new AudioParamValueParameter(
            'Wet', this.cross.fade, {
                documentation: "Controls the amount of (wet) effected signal, vs. dry (un-effected) signal.",
                defaultValue: 0.5,
                min: 0,
                max: 1});

        this.registerParameter(this.wetParameter);

        this.cross.connect(this.outGain);
        this.vInGain = new Tone.Gain(0.5);
        this.vIn =  context.createOscillator();
        this.vIn.frequency.value = 30;
        this.vIn.start(0);
        this.vInInverter1 = new Tone.Gain(-1);
        this.vInInverter2 = new Tone.Gain(-1);
        this.vInInverter3 = new Tone.Gain(-1);
        this.vInDiode1 = new Diode(sequencerEngine);
        this.vInDiode2 = new Diode(sequencerEngine);
        this.vcInverter1 = context.createGain();
        this.vcInverter1.gain.value = -1;
        this.vcDiode3 = new Diode(sequencerEngine);
        this.vcDiode4 = new Diode(sequencerEngine);
        this.compressor = new Tone.Compressor(-12, 3);

        this.vIn.connect(this.vInGain);
        /**
           vIn(OSC) -> vInGain -> vInInverter1 -> vInInverter2
                           |          |           |____>  vInDiode1
                           |          |                      |
                           |          |_____vInDiode2----> vInInverter3 --> COMPRESSOR
                           |                                        
                           |         wetIn -> COMPRESSOR
                           |           |
                           -----> vcInverter1 -> vcDiode3 
                           |                         |
                wetIn ---> |____> vcDiode4 ------> COMPRESSOR
         */

        this.wetIn.connect(this.vcInverter1); //
        this.wetIn.connect(this.vcDiode4.effectNode); //
        this.vcInverter1.connect(this.vcDiode3.effectNode); //`j$
        this.vInGain.connect(this.vInInverter1); //
        this.vInGain.connect(this.vcInverter1); // 
        this.vInGain.connect(this.vcDiode4.effectNode); //
        this.vInInverter1.connect(this.vInInverter2); // 
        this.vInInverter1.connect(this.vInDiode2.effectNode); // 
        this.vInInverter2.connect(this.vInDiode1.effectNode); // 
        this.vInDiode1.connect(this.vInInverter3); // 
        this.vInDiode2.connect(this.vInInverter3); // 
        
        this.vInInverter3.connect(this.compressor); // 
        this.vcDiode3.connect(this.compressor); // 
        this.vcDiode4.connect(this.compressor); // 
        this.compressor.connect(this.outGain);

        this.frequencyParameter= new AudioParamValueParameter(
            'Frequency', this.vIn.frequency, 
            {
                min: 1,
                max: 3000,
                unit: '',
                defaultValue: 30
            });
        this.registerParameter(this.frequencyParameter);

        this.distortionParameter = new AbstractParameter(
            'distortion', this, (obj, drive) => {
                this.vInDiode1.distortionParameter.set(drive);
                this.vInDiode2.distortionParameter.set(drive);
                this.vcDiode3.distortionParameter.set(drive);
                this.vcDiode4.distortionParameter.set(drive);
            },
            {
                documention: "Controls the level of saturation.",
                min: 2,
                max: 10,
                unit: '',
                defaultValue: 0.3,
                constantAutomationRate: true
            });
        this.registerParameter(this.distortionParameter);
        this.updateInitialValues();
    }

    connect(node) {
        this.cross.connect(node);
    }

    disconnect(node) {
        this.cross.disconnect();
    }

    getJson(isPreset) {
        let json = super.getJson();
        json.frequency = this.frequencyParameter.get();
        json.distortion = this.distortionParameter.get();
        json = this.saveParameterToJson(json, 'frequencyParameter', this.frequencyParameter, isPreset);
        json = this.saveParameterToJson(json, 'distortionParameter', this.distortionParameter, isPreset);
        return json;
    }

    fromJson(json, isCopy) {
        let bulk = super.fromJson(json, isCopy);
        bulkUpdate(this.frequencyParameter, json.frequency, bulk);
        bulkUpdate(this.distortionParameter, json.distortion, bulk);
        return {
            ... bulk,
            ... this.loadParameterFromJson(json, 'frequencyParameter', this.frequencyParameter, isCopy),
            ... this.loadParameterFromJson(json, 'distortionParameter', this.distortionParameter, isCopy)
        };
    }

    getEffectCopy(sequencerEngine=this.sequencerEngine, ignoreId=true) {
        var copy = new RingModulator(sequencerEngine);
        copy.fromJson(this.getJson(), ignoreId);
        return copy;
    }
}

export default RingModulator;
